import * as React from 'react';
import { Metadata } from '../../types';
import { useSelector } from '../../utils/useSelector';
import { getSchema } from '../../utils';
import { APIError } from '../../api';
import { ErrorName } from '../../enums/ErrorName';
import { AlertType } from '../../types/AlertType';
import { Alert } from 'rcd-cpp-ui/ui/alert/Alert';
import URLManager from '../../utils/urlManager';
import { useNavigate } from 'react-router-dom';
import { WorkspacePrefix } from '../../enums/WorkspacePrefix';
import { ResearchModelRefId } from '../../enums/ResearchModelRefId';
import { getDesiredWorkspace } from '../../utils/editModeUtils';

interface AdditionalInfo {
  itemRefId?: string;
  invalidDataRef: Metadata;
}

export class InvalidWorkspaceError extends APIError<AdditionalInfo> {
  constructor(
    message: string,
    info: AdditionalInfo,
    type: AlertType = 'error'
  ) {
    super(message, info, type, ErrorName.InvalidWorkspaceError);
  }
}

type Props = { error: InvalidWorkspaceError; onClose: () => void };

export const InvalidWorkspaceAlert = ({ error, onClose }: Props) => {
  const { itemRefId, invalidDataRef } = error?.additionalInfo || {};
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const itemType = useSelector(s => {
    const schema = getSchema(
      s.planning?.selectedWorkspace?.schemas,
      invalidDataRef.labels
    );
    const df = schema?.props?.displayField;
    return invalidDataRef.props[df] || 'this';
  });
  const invalidResearchModel =
    invalidDataRef.props?.refId == ResearchModelRefId.RESEARCH_BOARD;


   const homeWorkspaceOverride = itemRefId && !invalidResearchModel;
   const workspaces = useSelector(s => s.planning?.workspaces);

   const getValidWorkspace = React.useCallback(() => {
     return getDesiredWorkspace(workspaces, invalidDataRef);
   }, [workspaces]);
  const navigate = useNavigate();
  const [w, setW] = React.useState(null);
  const [id, setId] = React.useState(null);
  React.useEffect(() => {
    if (!w) {
      if (homeWorkspaceOverride) {
        getAPI()
          .getHomeWorkspace(itemRefId)
          .then(resp => {
            if (
              resp?.homeWorkspace?.props?.enableItemDataRefIds?.includes(
                invalidDataRef.props?.refId
              )
            ) {
              setW(resp.homeWorkspace);
            } else {
              setW(getValidWorkspace());
              setId(itemRefId);
            }
          });
      } else {
        setW(getValidWorkspace());
      }
    }
  }, [itemRefId, invalidResearchModel]);
  const url = id
    ? URLManager.generateUrl(w, { id })
    : homeWorkspaceOverride
      ? URLManager.generateUrl(w, { itemRefId })
      : URLManager.generateUrl(w);
  return (
    <Alert type={error.type as any} onClose={onClose}>
      <span>
        You cannot {itemRefId ? 'update' : 'create'} {itemType} items in this
        workspace.
      </span>
      {!!w && (
        <>
          <span> Please use </span>
          <span>
            {!!url && (
              <a
                href={url}
                onClick={e => {
                  e.preventDefault();
                  navigate(url);
                }}
              >
                {w?.props?.name} Workspace
              </a>
            )}
            {!url && <span>{w?.props?.name} Workspace</span>}.
          </span>
        </>
      )}
    </Alert>
  );
};
